<template>
  <div class="user-info-component">
    <!-- User Stats Section -->
    <StatsGrid v-if="showStats">
      <StatsCard
        title="Total Users"
        :value="stats ? stats.totalUsers : '…'"
        :detail="stats ? `${stats.deletedUsers} deleted` : undefined"
      />
      <StatsCard
        title="Premium Users"
        :value="stats ? `${stats.premiumUsers} / ${stats.giftedPremiumUsers}` : '…'"
        detail="Active / Gifted"
      />
    </StatsGrid>

    <!-- Users Table Section -->
    <div class="users-section">
      <div class="section-header">
        <h2>{{ title }}</h2>
        <div class="header-controls">
          <div class="search-container">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search users..."
              class="search-input"
            />
          </div>
          <button :disabled="loading" class="refresh-btn" @click="handleRefresh">
            {{ loading ? 'Loading...' : 'Refresh Users' }}
          </button>
        </div>
      </div>

      <div v-if="loading && users.length === 0" class="loading">Loading users...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="users.length === 0" class="loading">No users match your search.</div>
      <div v-else class="table-container" :class="{ stale: loading }">
        <table class="users-table">
          <thead>
            <tr>
              <th class="sortable-header" @click="sortBy('username')">
                User
                <span v-if="sortColumn === 'username'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th class="sortable-header email-column" @click="sortBy('email')">
                Email
                <span v-if="sortColumn === 'email'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th class="sortable-header" @click="sortBy('createdAt')">
                Created
                <span v-if="sortColumn === 'createdAt'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
              <th class="sortable-header" @click="sortBy('premium')">
                Premium
                <span v-if="sortColumn === 'premium'" class="sort-indicator">
                  {{ sortDirection === 'asc' ? '↑' : '↓' }}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id" class="user-row" @click="selectUser(user)">
              <td>
                <div class="user-cell">
                  <div class="username">{{ user.username }}</div>
                  <div class="user-badges">
                    <span v-if="user.isAdmin" class="badge badge-admin">Admin</span>
                    <span v-if="user.isSupporter" class="badge badge-supporter">Supporter</span>
                    <span v-if="user.studioAccess" class="badge badge-studio">Studio</span>
                    <span v-if="user.deleted" class="badge badge-deleted">Deleted</span>
                    <span v-if="user.bugReportsBlocked" class="badge badge-deleted"
                      >No reports</span
                    >
                  </div>
                </div>
              </td>
              <td class="email-column">
                <div class="email-cell">
                  <div class="email-address">{{ user.email }}</div>
                  <div class="user-badges">
                    <span
                      :class="['badge', user.emailVerified ? 'badge-verified' : 'badge-unverified']"
                    >
                      {{ user.emailVerified ? 'Verified' : 'Not Verified' }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="created-cell">{{ formatDate(user.createdAt) }}</td>
              <td class="premium-cell">
                <span v-if="hasActivePremium(user)" class="badge badge-premium">Premium</span>
                <span v-else-if="hasGiftedPremium(user)" class="badge badge-gifted">Gifted</span>
                <span v-else class="badge badge-free">Free</span>
                <span
                  v-if="user.activeSubscription?.managedPayments"
                  class="badge badge-mor"
                  title="Managed Payments — Link is the merchant of record"
                  >MoR</span
                >
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!error" class="pagination-bar">
        <span class="pagination-summary">
          <template v-if="total > 0">
            Showing {{ pageStart }}–{{ pageEnd }} of {{ total }}
            {{ searchQuery.trim() ? 'matching users' : 'users' }}
          </template>
          <template v-else>0 users</template>
        </span>
        <div class="pagination-controls">
          <label class="page-size">
            Per page
            <select v-model.number="pageSize">
              <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
            </select>
          </label>
          <button class="page-btn" :disabled="loading || page <= 1" @click="goToPage(1)">«</button>
          <button class="page-btn" :disabled="loading || page <= 1" @click="goToPage(page - 1)">
            ‹
          </button>
          <span class="page-indicator">Page {{ page }} of {{ pageCount }}</span>
          <button
            class="page-btn"
            :disabled="loading || page >= pageCount"
            @click="goToPage(page + 1)"
          >
            ›
          </button>
          <button
            class="page-btn"
            :disabled="loading || page >= pageCount"
            @click="goToPage(pageCount)"
          >
            »
          </button>
        </div>
      </div>
    </div>

    <!-- User Detail Modal -->
    <UserDetail
      v-if="selectedUser"
      :user="selectedUser"
      @close="selectedUser = null"
      @user-updated="onUserUpdated"
      @user-deleted="onUserDeleted"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import {
  client,
  parseErrorMessage,
  type AdminUser,
  type AdminUserSortColumn,
  type AdminUserStats,
} from '../client.js';
import UserDetail from './UserDetail.vue';
import StatsGrid from './StatsGrid.vue';
import StatsCard from './StatsCard.vue';

interface Props {
  title?: string;
  showStats?: boolean;
  autoLoad?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  title: 'All Users',
  showStats: true,
  autoLoad: true,
});

const emit = defineEmits<{
  'users-loaded': [users: AdminUser[]];
  'user-selected': [user: AdminUser];
  'user-updated': [user: AdminUser];
  'user-deleted': [userId: string];
  error: [error: string];
}>();

const PAGE_SIZES = [25, 50, 100, 200];
const SEARCH_DEBOUNCE_MS = 300;

// Server-driven list state: the table only ever holds the current page.
// Search / sort / paging are sent to the API (see server lib/adminUsers.ts).
const users = ref<AdminUser[]>([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(50);
const searchQuery = ref('');
const selectedUser = ref<AdminUser | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

// Global counts for the stat cards — separate endpoint, unaffected by search
const stats = ref<AdminUserStats | null>(null);

// Sorting state (defaults mirror the server: newest users first)
const sortColumn = ref<AdminUserSortColumn>('createdAt');
const sortDirection = ref<'asc' | 'desc'>('desc');

type SortableColumn = AdminUserSortColumn;

const pageCount = computed(() => Math.max(1, Math.ceil(total.value / pageSize.value)));
const pageStart = computed(() => (total.value === 0 ? 0 : (page.value - 1) * pageSize.value + 1));
const pageEnd = computed(() => Math.min(total.value, pageStart.value + users.value.length - 1));

// Helper functions
const hasActivePremium = (user: AdminUser): boolean => {
  return user.activeSubscription?.status === 'active' && !user.activeSubscription?.isGifted;
};

const hasGiftedPremium = (user: AdminUser): boolean => {
  return user.activeSubscription?.isGifted === true;
};

const sortBy = (column: SortableColumn) => {
  if (sortColumn.value === column) {
    // Toggle direction if same column
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    // New column: created is newest-first, everything else ascending
    sortColumn.value = column;
    sortDirection.value = column === 'createdAt' ? 'desc' : 'asc';
  }
  page.value = 1;
  loadUsers();
};

const goToPage = (target: number) => {
  const clamped = Math.min(pageCount.value, Math.max(1, target));
  if (clamped === page.value) return;
  page.value = clamped;
  loadUsers();
};

const formatDate = (date: Date) => {
  return date.toLocaleDateString();
};

const selectUser = (user: AdminUser) => {
  selectedUser.value = user;
  emit('user-selected', user);
};

const onUserUpdated = (updatedUser: AdminUser) => {
  // Update the user in the current page
  const index = users.value.findIndex((u) => u.id === updatedUser.id);
  if (index !== -1) {
    users.value[index] = updatedUser;
  }
  // Update the selected user
  selectedUser.value = updatedUser;
  // Premium / deleted flags may have changed
  loadStats();
  emit('user-updated', updatedUser);
};

const onUserDeleted = (deletedUserId: string) => {
  // Clear the selected user, then re-fetch so the page refills from the server
  selectedUser.value = null;
  loadUsers();
  loadStats();
  emit('user-deleted', deletedUserId);
};

// Monotonic request id so a slow earlier response can't overwrite a newer page
let requestSeq = 0;

const loadUsers = async () => {
  if (!client.value) return;

  const seq = ++requestSeq;
  loading.value = true;
  error.value = null;

  try {
    const result = await client.value.getUsers({
      page: page.value,
      pageSize: pageSize.value,
      search: searchQuery.value.trim() || undefined,
      sort: sortColumn.value,
      dir: sortDirection.value,
    });
    if (seq !== requestSeq) return; // superseded

    users.value = result.users;
    total.value = result.total;
    // Server clamps page/pageSize; mirror whatever it actually used
    page.value = result.page;
    pageSize.value = result.pageSize;

    // Landed past the last page (e.g. rows deleted): step back and retry once
    if (users.value.length === 0 && total.value > 0 && page.value > 1) {
      page.value = Math.max(1, Math.ceil(total.value / pageSize.value));
      loadUsers();
      return;
    }

    emit('users-loaded', users.value);
  } catch (err: any) {
    if (seq !== requestSeq) return;
    error.value = parseErrorMessage(err);
    emit('error', error.value);
    console.error('Error loading users:', err);
  } finally {
    if (seq === requestSeq) loading.value = false;
  }
};

const loadStats = async () => {
  if (!client.value || !props.showStats) return;
  try {
    stats.value = await client.value.getUserStats();
  } catch (err: any) {
    // Non-fatal: the table still works without the cards
    console.error('Error loading user stats:', err);
  }
};

const handleRefresh = () => {
  loadUsers();
  loadStats();
};

// Public methods (exposed to parent components)
const refreshUsers = () => {
  handleRefresh();
};

const clearSelection = () => {
  selectedUser.value = null;
};

// Expose methods to parent components
defineExpose({
  refreshUsers,
  clearSelection,
  users: computed(() => users.value),
  selectedUser: computed(() => selectedUser.value),
});

// Search: debounce keystrokes, always restart from page 1
let searchTimer: ReturnType<typeof setTimeout> | undefined;
watch(searchQuery, () => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    page.value = 1;
    loadUsers();
  }, SEARCH_DEBOUNCE_MS);
});

// Page size change: keep the first visible row roughly in place
watch(pageSize, (newSize, oldSize) => {
  if (newSize === oldSize) return;
  const firstRow = (page.value - 1) * oldSize;
  page.value = Math.floor(firstRow / newSize) + 1;
  loadUsers();
});

// Auto-load on mount if enabled
onMounted(() => {
  if (props.autoLoad) {
    loadUsers();
    loadStats();
  }
});
</script>

<style scoped>
.user-info-component {
  width: 100%;
}

.users-section {
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.section-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h2 {
  margin: 0;
  color: #1f2937;
}

.header-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.search-container {
  position: relative;
}

.search-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 200px;
  transition: border-color 0.2s;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.refresh-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.refresh-btn:hover:not(:disabled) {
  background: #2563eb;
}

.refresh-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.table-container {
  overflow-x: auto;
  transition: opacity 0.15s ease;
}

/* Previous page stays visible but dimmed while the next one loads */
.table-container.stale {
  opacity: 0.5;
  pointer-events: none;
}

.pagination-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid #e5e7eb;
  font-size: 0.875rem;
  color: #374151;
}

.pagination-summary {
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-size {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-right: 0.5rem;
  color: #6b7280;
}

.page-size select {
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  font-size: 0.875rem;
}

.page-btn {
  min-width: 2rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: white;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:hover:not(:disabled) {
  background: #f3f4f6;
}

.page-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.page-indicator {
  padding: 0 0.25rem;
  white-space: nowrap;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
}

.user-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.user-row:hover {
  background-color: #f3f4f6;
}

.users-table th,
.users-table td {
  padding: 0.75rem 0.5rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.users-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
  position: relative;
  padding-right: 1.5rem !important;
}

.sortable-header:hover {
  background: #f3f4f6 !important;
}

.sort-indicator {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: #6b7280;
}

.users-table td {
  color: #1f2937;
  vertical-align: top;
}

.user-cell {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.username {
  font-weight: 600;
  color: #1f2937;
}

.user-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.email-cell {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.email-address {
  font-weight: 500;
  color: #1f2937;
  font-size: 0.875rem;
}

.badge-verified {
  background: #d1fae5;
  color: #065f46;
}

.badge-unverified {
  background: #fef3c7;
  color: #92400e;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  white-space: nowrap;
}

.badge-admin {
  background: #fef3c7;
  color: #92400e;
}

.badge-supporter {
  background: #e0e7ff;
  color: #3730a3;
}

.badge-studio {
  background: #cffafe;
  color: #155e75;
}

.badge-premium {
  background: #d1fae5;
  color: #065f46;
}

/* Managed Payments (Link is merchant of record) */
.badge-mor {
  background: #e0e7ff;
  color: #3730a3;
}

.badge-gifted {
  background: #ddd6fe;
  color: #5b21b6;
}

.badge-free {
  background: #e5e7eb;
  color: #6b7280;
}

.badge-deleted {
  background: #fee2e2;
  color: #991b1b;
}

.loading {
  color: #6b7280;
  font-style: italic;
  text-align: center;
  padding: 2rem;
}

.error {
  color: #991b1b;
  background: #fee2e2;
  padding: 1rem;
  margin: 1.5rem;
  border-radius: 0.375rem;
  text-align: center;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .section-header {
    padding: 0.75rem;
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }

  .header-controls {
    flex-direction: column;
    gap: 0.75rem;
  }

  .search-input {
    width: 100%;
  }

  .pagination-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .pagination-controls {
    justify-content: space-between;
  }

  .users-table th,
  .users-table td {
    padding: 0.5rem 0.25rem;
    font-size: 0.875rem;
  }

  .users-table th {
    font-size: 0.75rem;
  }

  /* Hide email column on mobile */
  .email-column {
    display: none;
  }

  .user-cell {
    gap: 0.25rem;
  }

  .username {
    font-size: 0.875rem;
  }

  .badge {
    padding: 0.125rem 0.5rem;
    font-size: 0.625rem;
  }
}
</style>
